import { ProfileDto } from '@escrowtab/v2profile-api-client';
import { GetFullProfile, SetFullProfile } from '@et/guards';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';

import { ProfileState } from '@et/guards';
import { Store } from '@ngxs/store';
import {
  map,
  Observable,
  filter,
  first,
  switchMap,
  of,
  combineLatest,
  timeout,
  catchError,
  TimeoutError,
} from 'rxjs';
import { DatabaseService } from '@et/storage';
import { isOnline } from '@et/utils';

@Injectable({
  providedIn: 'root',
})
export class FullProfileResolver {
  private store = inject(Store);
  private dbService = inject(DatabaseService);
  private router = inject(Router);

  resolve(): Observable<boolean> {
    const profileFromAPI = this.store.select(ProfileState.getFullProfile);
    const profileId$ = this.store.select(ProfileState.v2ProfileId);

    return combineLatest([profileId$, profileFromAPI]).pipe(
      filter(([profileId]) => !!profileId),
      map(([profileId, profile]) => {
        if (!profile) {
          if (isOnline()) {
            this.store.dispatch(new GetFullProfile(profileId as string));
          } else {
            this.loadFromDB(profileId as string);
          }
        }
        return profile;
      }),
      filter((profile) => !!profile),
      switchMap((profile) => {
        return isOnline()
          ? this.dbService.profileService.saveAll([profile as ProfileDto])
          : of(profile);
      }),
      map((profile) => !!profile),
      first(),
      // Without this, a missing SetSelfProfile (no /self) left AuthCallback on
      // "Signing you in…" forever while this resolver waited on profileId.
      timeout({ first: 15_000 }),
      catchError((err) => {
        if (err instanceof TimeoutError) {
          void this.router.navigate(['/auth/login'], {
            queryParams: { error: 'session_timeout' },
          });
        }
        return of(false);
      }),
    );
  }
  /**
   * load profile from db
   *
   * @param {string} id
   * @memberof FullProfileResolver
   */
  async loadFromDB(id: string) {
    const profile = await this.dbService.profileService.get(id);
    this.store.dispatch(new SetFullProfile(profile as ProfileDto));
  }
}
